import { Component, Input, OnChanges, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { TUser } from 'src/app/project/modules/user/user.model';

import { EditNotificationsModalComponent } from 'src/app/project/modules/user/edit-notifications-modal/edit-notifications-modal.component';
import {
  notificationLabels,
  TNotificationLabels,
} from 'src/app/project/modules/user/user-settings/notification-labels';

import { DeviceService, WindowService } from '@core/services';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { SharesService } from 'src/app/project/modules/share/shares.service';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { SubscriptionsService, TSubscriptionsById } from '../../user/subscriptions.service';
import { UserService } from '../../user/user.service';

import { SortingService } from '@core/helpers';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { setChangedAccounts } from 'src/app/project/modules/account/account';
import { GET_SHARES } from 'src/app/project/modules/share/shares.store';
import { TEditNotificationModalData } from 'src/app/project/modules/user/edit-notifications-modal/edit-notification-modal.model';
import { setChangedWorkspaces } from 'src/app/project/modules/workspace/workspace';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { TWorkspaceSimpleResponse } from 'src/app/project/view-models/account-simple-response.model';
import { getUserLabels, TUserLabels } from '../../share/share-utils/user-labels';
import { TUpdateShareDTO } from '../../share/share.model';
import { DeleteWorkspace } from '../../workspace/workspace.actions';
import { SidePanelSitesListService } from '../side-panel-sites-list/side-panel-sites-list.service';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { getErrorMessage } from 'src/app/project/helpers/database/get-error-message';
import { ResponseErrorService } from '../../errors/response-error.service';

@Component({
  selector: 'pp-user-settings-table-container',
  templateUrl: './settings-table-container.component.html',
  styleUrls: ['./settings-table-container.component.scss'],
  standalone: false,
})
export class UserSettingsTableContainerComponent implements OnInit, OnChanges {
  @Input() ppWorkspaces: TWorkspaceSimpleResponse[];

  private readonly destroy$ = new Subject<void>();

  shareOptionLabels: TUserLabels = getUserLabels();
  notificationLabels: TNotificationLabels = notificationLabels;
  subscriptionsById: TSubscriptionsById;
  EIconPath = EIconPath;

  isMobile = false;

  user: TUser;
  workspaces: TWorkspaceSimpleResponse[] = [];

  deletingWorkspace: {
    [key: string]: boolean;
  } = {};

  constructor(
    private store: Store,
    private deviceService: DeviceService,
    private userService: UserService,
    private subscriptionsService: SubscriptionsService,
    private modalService: ModalService,
    private promptService: PromptService,
    private workspaceService: WorkspaceService,
    private translationPipe: TranslationPipe,
    private sortingService: SortingService,
    private sharesService: SharesService,
    private windowService: WindowService,
    private sidePanelSitesListService: SidePanelSitesListService,
    private responseErrorService: ResponseErrorService,
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.user = this.userService.getUser();
    this.subscriptionsById = this.subscriptionsService.getSubscriptionsById();
  }

  ngOnChanges(): void {
    this.workspaces = this.ppWorkspaces.sort((a, b) => {
      const sortingResult = this.sortingService.naturalSort(a.accountName, b.accountName);

      if (sortingResult !== 0) {
        return sortingResult;
      }

      return this.sortingService.naturalSort(a.name, b.name);
    });
  }

  editNotifications(workspace: TWorkspaceSimpleResponse): void {
    const share = GET_SHARES().find((_share) => _share.workspaceId === workspace.workspaceId);

    const data: TEditNotificationModalData = {
      workspaceId: workspace.workspaceId,
      userRef: share.userId,
      siteName: workspace.name,
      accountName: workspace.accountName,
    };

    this.modalService.setData(data);

    this.modalService.showModal(EditNotificationsModalComponent, {
      callback: () => {
        this.subscriptionsById = this.subscriptionsService.getSubscriptionsById();
      },
    });
  }

  removeWorkspace(workspaceId: string): void {
    const confirmationMessage = this.translationPipe.transform('prompt_site_deleted');

    if (!this.windowService.confirm(confirmationMessage)) {
      return;
    }

    this.deletingWorkspace[workspaceId] = true;

    this.workspaceService
      .deleteWorkspace(workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const promptText = this.translationPipe.transform('prompt_site_deleted');

          this.promptService.showSuccess(promptText);
        }),
        catchError(() => {
          const promptText = this.translationPipe.transform('save_changes_failed');

          this.promptService.showError(promptText);

          return of();
        }),
        finalize(() => {
          this.deletingWorkspace[workspaceId] = false;
        }),
      )
      .subscribe();
  }

  toggleSiteHidden(workspace: TWorkspaceSimpleResponse): void {
    workspace.hidden = !workspace.hidden;

    const shares = GET_SHARES();
    const share = shares.find((_share) => _share.workspaceId === workspace.workspaceId);

    const body: TUpdateShareDTO = {
      hidden: workspace.hidden,
    };

    this.sharesService
      .updateShare(share.shareId, body)
      .pipe(
        tap(() => {
          this.sidePanelSitesListService.markForRefresh();

          if (body.hidden) {
            this.store.dispatch(
              new DeleteWorkspace({
                workspaceId: workspace.workspaceId,
              }),
            );
          }
          const prompt = this.translationPipe.transform('site_visibility_change_success');

          this.promptService.showSuccess(prompt);
          setChangedWorkspaces(true);
          setChangedAccounts(true);
        }),
        catchError((error, caught) => {
          switch (error.status) {
            case EStatusCode.BAD_REQUEST:
            case EStatusCode.FORBIDDEN:
              getErrorMessage(error).then((message) => {
                this.promptService.showError(message);
              });
              break;
            default:
              const prompt = this.translationPipe.transform('site_visibility_change_error');
              this.promptService.showError(prompt);
              break;
          }
          return this.responseErrorService.handleRequestError(
            error,
            'Failed to update site visibility',
          );
        }),
      )
      .subscribe();
  }
}
